<template>
  <div :data-qa="`cart-shipping-option-${listingId}-${shippingId}`">
    <RevRadio
      :id="`cart-shipping-option-${listingId}-${shippingId}`"
      :key="optionKey"
      :data-qa="optionTestId"
      :model-value="checked"
      :value="shippingId"
      variant="full"
      @change="onChange"
    >
      <template #label>
        <div
          class="body-1 flex justify-between gap-12"
          :class="{ 'body-1-bold': checked }"
        >
          <span>
            {{
              i18n(translations.arrivalDatesShort, {
                earliest: earliestArrivalShortDate,
                latest: latestArrivalShortDate,
              })
            }}
          </span>
          <span :class="{ 'text-static-success-hi': isFree }">
            {{ price }}
          </span>
        </div>
      </template>

      <template #description>
        <div class="text-static-default-hi body-2">
          <div v-if="isCollectionPoint" class="flex gap-4">
            <IconLocationPinOutlined class="shrink-0" size="20" />
            <span>{{ i18n(translations.shippingPickup) }}</span>
          </div>
          <div v-else-if="isFree" class="flex gap-4">
            <IconTruck class="shrink-0" size="20" />
            <span>{{ i18n(translations.shippingStandard) }}</span>
          </div>
          <div v-else class="flex gap-4">
            <IconTruckExpress class="shrink-0" size="20" />
            <span>{{ i18n(translations.shippingExpress) }}</span>
          </div>
        </div>

        <div v-if="shouldDisplayCollectionPointAddress" class="mt-8">
          <div class="body-2-bold" data-qa="collection-point-name">
            {{ lastCollectionPoint?.selectedPoint.name }}
          </div>
          <div class="body-2 mt-4" data-qa="collection-point-address">
            {{ getSelectedCollectionPointAddress() }}
          </div>
        </div>
      </template>
    </RevRadio>

    <div
      v-if="shouldDisplayCollectionPointError"
      class="text-action-danger-hi body-2-bold mt-12"
    >
      {{ i18n(translations.mandatoryCollectionPoint) }}
    </div>

    <CollectionPointsModal
      v-if="shouldDisplayCollectionPointButton"
      :last-collection-point
      :listing-id
      :shipping-id
      @change="emit('change')"
    >
      <template #trigger="{ open }">
        <div class="mt-12 flex justify-end">
          <RevButton
            data-qa="collection-points-button"
            :icon="collectionPointButtonIcon"
            size="small"
            type="button"
            variant="secondary"
            @click="handleCollectionPointButtonClick(open)"
          >
            {{ i18n(collectionPointButtonLabel) }}
          </RevButton>
        </div>
      </template>
    </CollectionPointsModal>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { Address } from '@backmarket/nuxt-module-address/address'
import { useInlineFormatAddress } from '@backmarket/nuxt-module-address/useInlineFormatAddress'
import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevButton } from '@ds/components/Button'
import { RevRadio } from '@ds/components/Radio'
import { IconEdit } from '@ds/icons/IconEdit'
import { IconLocationPinOutlined } from '@ds/icons/IconLocationPinOutlined'
import { IconPackage } from '@ds/icons/IconPackage'
import { IconTruck } from '@ds/icons/IconTruck'
import { IconTruckExpress } from '@ds/icons/IconTruckExpress'

import { useDeliveryDateAdjustment } from '~/scopes/product/composables/useDeliveryDateAdjustement'

import CollectionPointsModal from '../CollectionPointsModal/CollectionPointsModal.vue'

import translations from './ShippingOption.translations'

const props = withDefaults(
  defineProps<{
    checked?: boolean
    earliestArrivalDate?: string
    isFree?: boolean
    isCollectionPoint?: boolean | undefined
    latestArrivalDate?: string
    listingId: string
    price?: string
    shipper?: string | undefined
    shippingId: string
    showCollectionPointError: boolean
    lastCollectionPoint?: ApiSpecs['/bm/cart']['GET']['response']['lastCollectionPoint']
  }>(),
  {
    checked: false,
    isCollectionPoint: false,
    isFree: false,
    earliestArrivalDate: '',
    latestArrivalDate: '',
    price: '',
    shipper: '',
    lastCollectionPoint: undefined,
  },
)

const emit = defineEmits(['change'])

const i18n = useI18n()
const tracking = useTracking()
const deliveryDateAdjustment = useDeliveryDateAdjustment()
const hasSelectedCollectionPoint = computed(() => {
  return !isEmpty(props.lastCollectionPoint?.selectedPoint)
})

const optionTestId = computed(() => {
  let name = 'unknown'

  if (props.isCollectionPoint) {
    name = 'shipping_option_pickup'
  } else if (props.isFree) {
    name = 'shipping_option_home_free'
  } else {
    name = 'shipping_option_home_paid'
  }

  return name
})

const optionKey = computed(() => {
  const collectionPointKey = hasSelectedCollectionPoint.value
    ? props.lastCollectionPoint?.selectedPoint.name
    : ''

  return `cart-shipping-option-${props.listingId}-${props.shippingId}-${collectionPointKey}`
})

const collectionPointButtonLabel = computed(() =>
  hasSelectedCollectionPoint.value
    ? translations.editCollectionPointButton
    : translations.chooseCollectionPointButton,
)

const collectionPointButtonIcon = computed(() =>
  hasSelectedCollectionPoint.value ? IconEdit : IconPackage,
)

function getSelectedCollectionPointAddress() {
  return props.lastCollectionPoint?.selectedPoint?.address
    ? useInlineFormatAddress(
        props.lastCollectionPoint?.selectedPoint.address as Address,
        {
          includeCompany: false,
          includeCountry: false,
        },
      )
    : ''
}

const shouldDisplayCollectionPointAddress = computed(
  () =>
    props.checked &&
    props.isCollectionPoint &&
    hasSelectedCollectionPoint.value,
)

const shouldDisplayCollectionPointButton = computed(
  () => props.checked && props.isCollectionPoint,
)

const shouldDisplayCollectionPointError = computed(
  () =>
    props.checked && props.isCollectionPoint && props.showCollectionPointError,
)

const earliestArrivalShortDate = computed(() => {
  return i18n.date(deliveryDateAdjustment(props.earliestArrivalDate), {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  })
})

const latestArrivalShortDate = computed(() => {
  return i18n.date(new Date(props.latestArrivalDate), {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  })
})

const handleCollectionPointButtonClick = (open: () => void) => {
  open()

  tracking.trackClick({
    zone: 'cart',
    name: hasSelectedCollectionPoint.value
      ? 'edit_collection_point'
      : 'choose_collection_point',
  })
}

function onChange() {
  let name = 'unknown'

  if (props.isCollectionPoint) {
    name = 'shipping_option_pickup'
  } else if (props.isFree) {
    name = 'shipping_option_home_free'
  } else {
    name = 'shipping_option_home_paid'
  }

  tracking.trackClick({
    zone: 'checkout_address_confirmation',
    name,
  })

  emit('change')
}
</script>
